import React, { useCallback, useEffect, useRef } from 'react';
interface GoldParticlesProps {
  density?: number;
  speed?: number;
  className?: string;
}
interface Particle {
  x: number;
  y: number;
  size: number;
  opacity: number;
  speed: number;
  sway: number;
  swaySpeed: number;
  swayOffset: number;
  color: string;
}
const GOLD_COLORS = ['#D4AF37', '#E8C547', '#F5E6A3', '#D4AF37', '#E8C547'];
export function GoldParticles({
  density = 60,
  speed = 1,
  className = ''
}: GoldParticlesProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>(0);
  const timeRef = useRef<number>(0);
  const initParticles = useCallback((width: number, height: number) => {
    const particles: Particle[] = [];
    for (let i = 0; i < density; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 3 + 1,
        opacity: Math.random() * 0.5 + 0.2,
        speed: (Math.random() * 0.5 + 0.2) * speed,
        sway: Math.random() * 30 + 10,
        swaySpeed: Math.random() * 0.002 + 0.001,
        swayOffset: Math.random() * Math.PI * 2,
        color: GOLD_COLORS[Math.floor(Math.random() * GOLD_COLORS.length)]
      });
    }
    particlesRef.current = particles;
  }, [density, speed]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const {
          width,
          height
        } = entry.contentRect;
        canvas.width = width;
        canvas.height = height;
        if (particlesRef.current.length === 0) {
          initParticles(width, height);
        }
      }
    });
    resizeObserver.observe(canvas.parentElement || canvas);
    const animate = () => {
      const {
        width,
        height
      } = canvas;
      ctx.clearRect(0, 0, width, height);
      timeRef.current += 1;
      particlesRef.current.forEach(p => {
        p.y += p.speed;
        const swayX = Math.sin(timeRef.current * p.swaySpeed + p.swayOffset) * p.sway;
        if (p.y > height + 10) {
          p.y = -10;
          p.x = Math.random() * width;
        }
        ctx.beginPath();
        ctx.arc(p.x + swayX, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.opacity;
        ctx.fill();
        ctx.globalAlpha = 1;
      });
      animationRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      cancelAnimationFrame(animationRef.current);
      resizeObserver.disconnect();
    };
  }, [initParticles]);
  return <canvas ref={canvasRef} className={`absolute inset-0 pointer-events-none ${className}`} style={{
    willChange: 'transform'
  }} aria-hidden="true" data-id="element-39" />;
}